import type { FormField } from "@/hooks/schema/use-schema";
import type { EntitySchema } from "@/services/entity-instance.service";
import type { DataTypeEnum, EntityField } from "@/services/entity.service";
import type { FieldVariantsData } from "@/services/schema-helpers";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import equal from "fast-deep-equal";
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// System fields that are not user-editable. Used to hide fields from forms and filter them from payloads.
export const systemFields = ["id", "created_at", "updated_at", "integer_id"];

/** Every edit path must check this: system and formula fields are computed by the backend. */
export const isReadOnlyField = (fieldName: string, formula?: string | null) =>
  systemFields.includes(fieldName) || !!formula;

// TODO: use the version of the utils package everywehere and delete this
export const paginationDataStr = `pagination {
  current_page
  has_more_pages
  last_page
  total
  per_page
}
`;

// TODO: Re-enable total when backend bug with to-many relation total calculation is fixed
export const paginationDataStrWithoutTotal = `pagination {
  current_page
  has_more_pages
  per_page
}
`;

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const removeKeysFromObject = (obj: object, keysToRemove: Array<string>) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => {
      // Keep null values so we can explicitly send null to backend
      return !keysToRemove.includes(key);
    }),
  );
};

export const isGraphQlErrorReponse = (error: unknown) => {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "status" in error.response
  ) {
    return true;
  }
};

// we use this funciton to parse the inputs for the backend to the correct data types from the text input fields
export const parseValues = (objectData: object, fields: Array<FormField>): object => {
  const newData = { ...objectData };
  Object.keys(objectData).forEach((x) => {
    const field = fields.find((f) => f.name === x);
    if (field?.type === "FieldVariantsData") {
      const origField = fields.find((f) => f.name === x.replace(/_variants$/, ""));
      // @ts-expect-error because of the generalization
      newData[x] = (newData[x] as FieldVariantsData).data.map((x) => ({
        variant: x.variant,
        value: parseValue(x.value as string, origField?.type ?? ""),
      }));
      return;
    }
    // @ts-expect-error because of the generalization
    newData[x] = parseValue(
      // @ts-expect-error because of the generalization
      newData[x],
      field?.type ?? "",
    );
  });
  return newData;
};

export const parseValue = (value: string, scalar: DataTypeEnum | string): unknown | undefined => {
  switch (scalar.toUpperCase() as string) {
    case "INT":
      if (!value) {
        return null;
      }
      {
        const parsed = parseInt(value, 10);
        return parsed;
      }
    case "DECIMAL":
    case "FLOAT":
      if (!value) {
        return null;
      }
      {
        const parsed = Number(value);
        return parsed;
      }
    case "DATE":
    case "DATETIME":
      if (!value) {
        return null;
      }
      return value;
    case "TEXT":
    default:
      return value;
  }
};

export const checkValidity = (value: string, scalar: DataTypeEnum): boolean => {
  switch (scalar.toUpperCase() as string) {
    case "INT":
      if (!value) {
        return true;
      }
      {
        const num = Number(value);
        const isValid = Number.isInteger(num);

        return isValid;
      }
    case "DECIMAL":
    case "FLOAT":
      if (!value) {
        return true;
      }
      {
        const num = Number(value);
        const isValid = Number.isFinite(num);

        return isValid;
      }
    case "TEXT":
    default:
      return true;
  }
};

export const validateField = (
  value: string | number | undefined | null,
  validationString?: string,
): { isValid: boolean; errors: string[] } => {
  if (!validationString) {
    return { isValid: true, errors: [] };
  }

  const stringValue = value?.toString() ?? "";
  const validations = validationString.split(";");
  const errors: string[] = [];

  // Determine presence of high-level flags upfront
  const hasRequired = validations.some((v) => v.trim() === "required");
  const hasNullable = validations.some((v) => v.trim() === "nullable");

  // Handle empty values before per-rule checks to avoid conflicting outcomes
  if (!stringValue || stringValue.trim() === "") {
    if (hasRequired) {
      errors.push("This field is required");
      return { isValid: false, errors };
    }
    // If nullable (or neither flag) and empty -> valid, no need to run other checks
    if (hasNullable || !hasRequired) {
      return { isValid: true, errors: [] };
    }
  }

  // Check each validation rule
  for (const validation of validations) {
    const rule = validation.trim();

    // Skip explicit required/nullable handling here since we've already handled empty state

    // Regex check
    if (rule.startsWith("regex:")) {
      // Extract pattern after "regex:" prefix
      let pattern = rule.substring(6); // Remove "regex:" (6 characters)
      // Strip Laravel regex delimiters (forward slashes) if present
      // Laravel stores regex as /pattern/, but JavaScript RegExp expects just the pattern
      if (pattern.startsWith("/") && pattern.endsWith("/")) {
        pattern = pattern.slice(1, -1);
      }
      try {
        const regex = new RegExp(pattern);
        if (!regex.test(stringValue)) {
          errors.push("Invalid format");
        }
      } catch {
        errors.push("Invalid format");
      }
    }
  }

  return { isValid: errors.length === 0, errors };
};

export const ensureFirstLetterLowercase = (str: string) => {
  if (!str || typeof str !== "string") {
    throw new Error("Input must be a non-empty string");
  }

  return str.charAt(0).toLowerCase() + str.slice(1);
};

export const isSchemaList = (schema?: EntitySchema) => {
  if (schema) {
    return schema.fields.some((x) => x.list);
  }
  return false;
};

export const cleanupEntityName = (entityName: string) => {
  if (entityName.endsWith("ListData")) {
    return entityName.replace("ListData", "");
  } else if (entityName.endsWith("Data")) {
    return entityName.replace("Data", "");
  } else return entityName;
};

export const isUUID = (content: string) => {
  const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regex.test(content);
};

// Default width and height for entity view fields based on data type
export const defaultWidthAndHeightForDataType = (
  dataType: DataTypeEnum,
): {
  width: 1 | 2 | 3;
  height: 1 | 2 | 3;
} => {
  switch (dataType) {
    case "TEXT":
      return { width: 1, height: 1 };
    case "LONGTEXT":
      return { width: 3, height: 1 };
    case "RICHTEXT":
      return { width: 3, height: 3 };
    case "INT":
      return { width: 1, height: 1 };
    case "FLOAT":
      return { width: 1, height: 1 };
    case "BOOLEAN":
      return { width: 1, height: 1 };
    case "DATE":
      return { width: 1, height: 1 };
    case "DATETIME":
      return { width: 1, height: 1 };
    case "TIME":
      return { width: 1, height: 1 };
    case "JSON":
      return { width: 3, height: 3 };
    case "OBJECT":
      return { width: 3, height: 1 };
    default:
      return { width: 3, height: 1 };
  }
};

const defaultFieldOrder = ["name", "type"];
export const getDefaultField = (entityName: string, fields: Array<EntityField | FormField>) => {
  if (entityName.toLocaleLowerCase() === "pylouser") {
    return "email";
  }

  for (const defaultField of defaultFieldOrder) {
    const f = fields.find((x) => x.name.toLocaleLowerCase() == defaultField);
    if (f) {
      return f.name;
    }
  }

  //return first text field but not id
  for (const f of fields) {
    if ("data_type" in f && f.data_type === "TEXT" && f.name !== "id") {
      return f.name;
    } else if ("type" in f && f.type === "String" && f.name !== "id") {
      return f.name;
    }
  }

  const integer_id = fields.find((x) => x.name === "integer_id");
  if (integer_id) {
    return integer_id.name;
  }

  return "id";
};

/**
 * Resolve a (possibly deep) display-field path against a fetched instance.
 * Scalars are read directly; to-one relations are nested as `{ data: {...} }`
 * (the GraphQL envelope), so we step into `.data` between segments. Returns
 * `undefined` if any hop is missing. Example: `"customer.region.name"` reads
 * `instance.customer.data.region.data.name`.
 */
export const getInstanceDisplayValue = (instance: unknown, path: string): unknown => {
  if (!instance || !path) return undefined;
  let current: unknown = instance;
  for (const segment of path.split(".")) {
    if (!current || typeof current !== "object") return undefined;
    const obj = current as Record<string, unknown>;
    // Unwrap the relation envelope when the segment lives under `.data`.
    const scope =
      !(segment in obj) && obj.data && typeof obj.data === "object"
        ? (obj.data as Record<string, unknown>)
        : obj;
    current = scope[segment];
  }
  return current;
};

export const compareArray = <TData extends { id?: string }>(
  orig: Array<TData>,
  compare: Array<TData>,
) => {
  const changedArrays: Array<TData> = [];
  compare.forEach((c) => {
    if (!c.id) {
      changedArrays.push(c);
      return;
    }
    const o = orig.find((x) => x.id === c.id);
    if (!equal(o, c)) {
      changedArrays.push(c);
    }
  });
  return changedArrays;
};
